/**
 * V2.2 — Cmd+K command palette.
 *
 * cmdk-backed overlay with sections: Actions / Navigate / Recent /
 * Search-everything. Single hardcoded Cmd+K (Mac) / Ctrl+K (other)
 * binding — broader keyboard ecosystem deferred until telemetry shows
 * which shortcuts operators actually want.
 *
 * Sections are passive: this component just renders what the host
 * provides. It does not own the data; the workspace composes the
 * commands list. Keeps the palette decoupled from any specific store.
 */
import React, { useEffect, useState } from "react";
import { Command } from "cmdk";

export interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  /** Section header this command belongs under. Default "Actions". */
  section?: string;
  /** Icon glyph (3-char string) shown to the left of the label. */
  icon?: string;
  /**
   * Run when the operator selects this command. The palette closes
   * automatically after `onRun` returns (or is invoked).
   */
  onRun: () => void;
  /** Optional keyboard shortcut hint shown to the right (decorative). */
  shortcut?: string;
}

export interface CommandPaletteProps {
  /** All commands to surface. Sectioned by `section`. */
  commands: CommandItem[];
  /** Called when the palette is dismissed without selection. */
  onClose?: () => void;
  /** Override the default Cmd+K binding (e.g. for testing). */
  hotkey?: { meta: boolean; key: string };
}

function isModifierMatch(e: KeyboardEvent, hotkey: { meta: boolean; key: string }): boolean {
  if (e.key.toLowerCase() !== hotkey.key.toLowerCase()) return false;
  return hotkey.meta ? e.metaKey || e.ctrlKey : !e.metaKey && !e.ctrlKey;
}

// V2 BugBot fix: module-scoped default so React's referential equality on
// the [hotkey] effect dependency is stable across renders. Earlier the
// default was an inline `{ meta: true, key: "k" }` literal in the prop
// destructure, which generated a new object every render and re-registered
// the global keydown listener on every parent state change.
const DEFAULT_HOTKEY = { meta: true, key: "k" } as const;

/**
 * Stateful container — owns its own open/close state via a global
 * Cmd+K (or Ctrl+K) listener. Pass `commands` from the host.
 */
export default function CommandPalette({
  commands,
  onClose,
  hotkey = DEFAULT_HOTKEY,
}: CommandPaletteProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isModifierMatch(e, hotkey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hotkey]);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  // Group commands by section, preserving first-seen order.
  const sections = new Map<string, CommandItem[]>();
  for (const cmd of commands) {
    const key = cmd.section ?? "Actions";
    const list = sections.get(key) ?? [];
    list.push(cmd);
    sections.set(key, list);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Command palette"
      aria-modal="true"
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "12vh",
        backgroundColor: "rgba(0, 0, 0, 0.4)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 90vw)",
          maxHeight: "70vh",
          borderRadius: 10,
          backgroundColor: "var(--c-surface)",
          border: "1px solid var(--c-border)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
        }}
      >
        <Command label="Command palette" loop>
          <Command.Input
            autoFocus
            placeholder="Type a command or search…"
            style={{
              width: "100%",
              padding: "14px 16px",
              fontSize: 14,
              backgroundColor: "var(--c-surface)",
              color: "var(--c-text)",
              border: "none",
              borderBottom: "1px solid var(--c-border)",
              outline: "none",
            }}
          />
          <Command.List
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              padding: "6px",
            }}
          >
            <Command.Empty
              style={{
                padding: "24px 16px",
                fontSize: 12,
                color: "var(--c-text-faint)",
                textAlign: "center",
              }}
            >
              No commands match.
            </Command.Empty>
            {Array.from(sections.entries()).map(([section, items]) => (
              <Command.Group
                key={section}
                heading={section}
                style={
                  {
                    "--cmdk-group-heading-padding": "6px 10px",
                  } as React.CSSProperties
                }
              >
                {items.map((cmd) => (
                  <Command.Item
                    key={cmd.id}
                    value={`${cmd.label} ${cmd.hint ?? ""}`}
                    onSelect={() => {
                      cmd.onRun();
                      handleClose();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      borderRadius: 6,
                      fontSize: 13,
                      color: "var(--c-text)",
                      cursor: "pointer",
                    }}
                  >
                    {cmd.icon && (
                      <span
                        aria-hidden="true"
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10,
                          color: "var(--c-text-faint)",
                          padding: "2px 4px",
                          borderRadius: 3,
                          backgroundColor: "var(--c-surface-2)",
                          border: "1px solid var(--c-border)",
                        }}
                      >
                        {cmd.icon}
                      </span>
                    )}
                    <span style={{ flex: 1 }}>{cmd.label}</span>
                    {cmd.hint && (
                      <span style={{ fontSize: 11, color: "var(--c-text-faint)" }}>{cmd.hint}</span>
                    )}
                    {cmd.shortcut && (
                      <kbd
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10,
                          color: "var(--c-text-faint)",
                          padding: "2px 6px",
                          borderRadius: 3,
                          backgroundColor: "var(--c-surface-2)",
                          border: "1px solid var(--c-border)",
                        }}
                      >
                        {cmd.shortcut}
                      </kbd>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
